import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { recordConsent, fetchCurrentConsent, type ConsentRecord } from '../api/consent'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ConsentCategories {
  analytics: boolean
  marketing: boolean
}

interface ConsentContextValue {
  /** True while we haven't loaded or saved consent yet */
  isPending: boolean
  /** Current accepted categories */
  categories: ConsentCategories
  /** Save new consent choices */
  saveConsent: (categories: ConsentCategories) => Promise<void>
  /** Accept all categories */
  acceptAll: () => Promise<void>
  /** Accept functional only */
  acceptNecessary: () => Promise<void>
  /** Current policy version stored in the latest record */
  policyVersion: string
}

const CONSENT_SESSION_KEY = 'gcg_consent_session'
const CONSENT_CACHE_KEY = 'gcg_consent'
const CONSENT_CACHE_VERSION = 2
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000
// Cookie name — clears with "clear cookies", unlike localStorage
const CONSENT_ACK_COOKIE = 'gcg_consent_ack'

const ConsentContext = createContext<ConsentContextValue | null>(null)

function readConsentCookie(): boolean {
  try {
    if (typeof document === 'undefined') return false
    return document.cookie.split(';').some(c => c.trim().startsWith(`${CONSENT_ACK_COOKIE}=`))
  } catch {
    return false
  }
}

function writeConsentCookie(): void {
  try {
    const maxAge = 365 * 24 * 60 * 60
    document.cookie = `${CONSENT_ACK_COOKIE}=1; path=/; max-age=${maxAge}; SameSite=Lax`
  } catch {
    // cookie write unavailable
  }
}

function getOrCreateSessionToken(): string {
  try {
    const existing = localStorage.getItem(CONSENT_SESSION_KEY)
    if (existing) return existing
    const token = crypto.randomUUID()
    localStorage.setItem(CONSENT_SESSION_KEY, token)
    return token
  } catch {
    return 'no-storage'
  }
}

function readCachedConsent(): ConsentRecord | null {
  // Cookie cleared (user cleared site cookies) → treat as new visitor.
  // Also discard the session token so the backend sees a fresh session.
  if (!readConsentCookie()) {
    try { localStorage.removeItem(CONSENT_SESSION_KEY) } catch { /* unavailable */ }
    return null
  }
  try {
    const raw = localStorage.getItem(CONSENT_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ConsentRecord & { cachedAt: number; v?: number }
    // Invalidate caches from old schema versions (snake_case, data-wrapped, etc.)
    if (parsed.v !== CONSENT_CACHE_VERSION) {
      localStorage.removeItem(CONSENT_CACHE_KEY)
      return null
    }
    if (Date.now() - parsed.cachedAt > CONSENT_EXPIRY_MS) {
      localStorage.removeItem(CONSENT_CACHE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeCachedConsent(record: ConsentRecord): void {
  writeConsentCookie()
  try {
    localStorage.setItem(CONSENT_CACHE_KEY, JSON.stringify({ ...record, cachedAt: Date.now(), v: CONSENT_CACHE_VERSION }))
  } catch {
    // storage unavailable
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [isPending, setIsPending] = useState(true)
  const [categories, setCategories] = useState<ConsentCategories>({ analytics: false, marketing: false })
  const [policyVersion, setPolicyVersion] = useState('')

  useEffect(() => {
    const cached = readCachedConsent()
    if (cached && !cached.isPending) {
      setCategories({ analytics: cached.analytics, marketing: cached.marketing })
      setPolicyVersion(cached.policyVersion)
      setIsPending(false)
      return
    }

    const token = getOrCreateSessionToken()
    fetchCurrentConsent(token)
      .then(record => {
        if (!record.isPending) {
          setCategories({ analytics: record.analytics, marketing: record.marketing })
          setPolicyVersion(record.policyVersion)
          writeCachedConsent(record)
        }
        setIsPending(record.isPending)
      })
      .catch(() => {
        setIsPending(true)
      })
  }, [])

  const saveConsent = useCallback(async (cats: ConsentCategories) => {
    const token = getOrCreateSessionToken()
    const record = await recordConsent({
      analytics: cats.analytics,
      marketing: cats.marketing,
      sessionToken: token,
    })
    setCategories({ analytics: record.analytics, marketing: record.marketing })
    setPolicyVersion(record.policyVersion)
    writeCachedConsent(record)
    setIsPending(false)
  }, [])

  const acceptAll = useCallback(() => saveConsent({ analytics: true, marketing: true }), [saveConsent])
  const acceptNecessary = useCallback(() => saveConsent({ analytics: false, marketing: false }), [saveConsent])

  return (
    <ConsentContext.Provider value={{ isPending, categories, saveConsent, acceptAll, acceptNecessary, policyVersion }}>
      {children}
    </ConsentContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext)
  if (!ctx) throw new Error('useConsent must be used inside ConsentProvider')
  return ctx
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConsentGate(category: 'analytics' | 'marketing'): boolean {
  const { categories, isPending } = useConsent()
  return !isPending && categories[category]
}
